import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { toast } from "sonner";
import { ExternalLink, Plus, X } from "lucide-react";

const DIMS = [["category", "Categories"], ["city", "Cities"], ["language", "Languages"], ["platform", "Platforms"]];

function SettingsCard({ onSaved }) {
  const [s, setS] = useState(null);
  const [adds, setAdds] = useState({});
  useEffect(() => { api.get("/admin/seo/settings").then((r) => setS(r.data)).catch(() => {}); }, []);
  if (!s) return <p className="text-sm text-muted-foreground">Loading settings…</p>;
  const setDim = (d, rows) => setS({ ...s, dimensions: { ...s.dimensions, [d]: rows } });
  const save = async () => {
    try { const { data } = await api.put("/admin/seo/settings", s); setS(data); toast.success("SEO settings saved."); onSaved(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not save."); }
  };
  return (
    <div className="rounded-2xl border border-border bg-surface p-5 mb-8" data-testid="seo-settings">
      <div className="flex flex-wrap items-end gap-6 mb-5">
        <label className="text-sm">Min. profiles to index
          <Input type="number" min={1} value={s.threshold} onChange={(e) => setS({ ...s, threshold: Number(e.target.value) })} data-testid="seo-threshold-input" className="mt-1 w-28 h-10 bg-background" />
        </label>
        <label className="flex items-center gap-2 text-sm"><Switch checked={s.auto_approve} onCheckedChange={(v) => setS({ ...s, auto_approve: v })} data-testid="seo-auto-approve-switch" /> Auto-approve pages that meet the threshold</label>
        <label className="flex items-center gap-2 text-sm"><Switch checked={s.include_demo} onCheckedChange={(v) => setS({ ...s, include_demo: v })} data-testid="seo-include-demo-switch" /> Include demo creators</label>
        <Button onClick={save} data-testid="seo-settings-save" className="rounded-full bg-brand-pink text-white ml-auto">Save settings</Button>
      </div>
      <div className="grid md:grid-cols-2 xl:grid-cols-4 gap-5">
        {DIMS.map(([d, label]) => (
          <div key={d} data-testid={`seo-dim-${d}`}>
            <p className="font-semibold text-sm mb-2">{label} <span className="text-xs text-muted-foreground font-normal">({(s.dimensions[d] || []).length})</span></p>
            <div className="flex flex-wrap gap-1.5 mb-2 max-h-56 overflow-y-auto pr-1">
              {(s.dimensions[d] || []).map((it, i) => (
                <span key={it.name} title={it.region || undefined} className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs ${it.enabled ? "border-brand-pink/40 bg-brand-pink/10" : "border-border text-muted-foreground line-through"}`}>
                  <button onClick={() => setDim(d, s.dimensions[d].map((x, j) => (j === i ? { ...x, enabled: !x.enabled } : x)))} title="Enable / disable">{it.name}</button>
                  <button onClick={() => setDim(d, s.dimensions[d].filter((_, j) => j !== i))} aria-label={`Remove ${it.name}`}><X className="h-3 w-3" /></button>
                </span>
              ))}
            </div>
            <div className="flex gap-1">
              <Input value={adds[d] || ""} onChange={(e) => setAdds({ ...adds, [d]: e.target.value })} placeholder={`Add ${label.toLowerCase().slice(0, -1)}`} data-testid={`seo-dim-add-input-${d}`} className="h-8 text-xs bg-background" />
              <Button size="sm" variant="outline" data-testid={`seo-dim-add-btn-${d}`} className="h-8" onClick={() => { if (adds[d]?.trim()) { setDim(d, [...(s.dimensions[d] || []), { name: adds[d].trim(), enabled: true }]); setAdds({ ...adds, [d]: "" }); } }}><Plus className="h-3 w-3" /></Button>
            </div>
          </div>
        ))}
      </div>
      <p className="text-xs text-muted-foreground mt-4">Click a value to enable/disable it. Changes apply after saving.</p>
    </div>
  );
}

function PageEditor({ page, onClose, onSaved }) {
  const [f, setF] = useState(null);
  useEffect(() => { setF(page ? { status: page.status, title: page.title || "", description: page.description || "", intro: page.intro || "", featured: (page.featured_ids || []).join(", ") } : null); }, [page]);
  if (!page || !f) return null;
  const save = async () => {
    try {
      await api.put("/admin/seo/pages", { path: page.path, status: f.status, title: f.title, description: f.description, intro: f.intro, featured_ids: f.featured.split(",").map((x) => x.trim()).filter(Boolean) });
      toast.success("Page saved."); onSaved(); onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not save page."); }
  };
  return (
    <Sheet open onOpenChange={(o) => !o && onClose()}>
      <SheetContent className="w-full sm:max-w-lg overflow-y-auto bg-background" data-testid="seo-page-editor">
        <SheetHeader><SheetTitle className="font-heading text-xl">{page.label}</SheetTitle></SheetHeader>
        <p className="text-xs text-muted-foreground mb-4 break-all">{page.canonical_url} · {page.count} profiles</p>
        <div className="space-y-4">
          <div className="flex gap-2">
            {["auto", "approved", "disabled"].map((st) => (
              <button key={st} onClick={() => setF({ ...f, status: st })} data-testid={`seo-page-status-${st}`}
                className={`px-3 py-1 rounded-full text-xs font-semibold border capitalize ${f.status === st ? "bg-brand-pink text-white border-brand-pink" : "border-border"}`}>{st}</button>
            ))}
          </div>
          <label className="block text-sm">Custom title <span className="text-xs text-muted-foreground">({f.title.length}/70)</span>
            <Input value={f.title} maxLength={70} onChange={(e) => setF({ ...f, title: e.target.value })} data-testid="seo-page-title-input" placeholder="Leave empty to auto-generate" className="mt-1 bg-surface" /></label>
          <label className="block text-sm">Meta description <span className="text-xs text-muted-foreground">({f.description.length}/165)</span>
            <Textarea value={f.description} maxLength={200} onChange={(e) => setF({ ...f, description: e.target.value })} data-testid="seo-page-desc-input" placeholder="Leave empty to auto-generate" className="mt-1 bg-surface" /></label>
          <label className="block text-sm">Intro paragraph
            <Textarea value={f.intro} onChange={(e) => setF({ ...f, intro: e.target.value })} data-testid="seo-page-intro-input" placeholder="Leave empty to auto-generate from data" className="mt-1 bg-surface min-h-[110px]" /></label>
          <label className="block text-sm">Featured creator IDs (comma-separated, shown first)
            <Input value={f.featured} onChange={(e) => setF({ ...f, featured: e.target.value })} data-testid="seo-page-featured-input" placeholder="user_abc123, user_def456" className="mt-1 bg-surface" /></label>
          <Button onClick={save} data-testid="seo-page-save" className="w-full rounded-full bg-brand-pink text-white">Save page</Button>
        </div>
      </SheetContent>
    </Sheet>
  );
}

export function AdminSeo() {
  const [rows, setRows] = useState(null);
  const [q, setQ] = useState("");
  const [only, setOnly] = useState("");
  const [edit, setEdit] = useState(null);
  const load = () => api.get("/admin/seo/pages", { params: { q: q || undefined, only: only || undefined } }).then((r) => setRows(r.data.pages)).catch(() => setRows([]));
  useEffect(() => { const t = setTimeout(load, 300); return () => clearTimeout(t); }, [q, only]); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <div data-testid="admin-seo-tab">
      <SettingsCard onSaved={() => setTimeout(load, 500)} />
      <div className="flex flex-col sm:flex-row gap-3 mb-4">
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Filter by URL, e.g. mumbai" data-testid="seo-pages-search" className="h-10 bg-surface sm:max-w-xs" />
        <div className="flex gap-2">
          {[["", "All candidates"], ["indexable", "Indexable only"]].map(([k, l]) => (
            <button key={l} onClick={() => setOnly(k)} data-testid={`seo-pages-filter-${k || "all"}`} className={`px-3 h-10 rounded-full text-xs font-semibold border ${only === k ? "bg-foreground text-background border-foreground" : "border-border"}`}>{l}</button>
          ))}
        </div>
        <a href={`${process.env.REACT_APP_BACKEND_URL}/api/sitemap.xml`} target="_blank" rel="noreferrer" data-testid="seo-sitemap-link" className="sm:ml-auto text-sm text-brand-pink inline-flex items-center gap-1">View sitemap <ExternalLink className="h-3.5 w-3.5" /></a>
      </div>
      <div className="rounded-2xl border border-border bg-surface overflow-x-auto">
        <table className="w-full text-sm" data-testid="seo-pages-table">
          <thead className="text-xs text-muted-foreground uppercase tracking-wider"><tr className="border-b border-border">
            <th className="text-left p-3">Page / URL</th><th className="text-left p-3">Profiles</th><th className="text-left p-3">Status</th><th className="text-left p-3">Indexable</th><th className="p-3" />
          </tr></thead>
          <tbody>
            {(rows || []).map((r) => (
              <tr key={r.path} className="border-b border-border last:border-0" data-testid={`seo-page-row-${r.path}`}>
                <td className="p-3 min-w-[240px]"><p className="font-semibold">{r.label}</p><a href={r.path} target="_blank" rel="noreferrer" className="text-xs text-brand-pink break-all">{r.path}</a>{(r.title || r.intro) && <span className="ml-2 text-[10px] text-muted-foreground">(edited)</span>}</td>
                <td className="p-3">{r.count}</td>
                <td className="p-3 capitalize">{r.status}</td>
                <td className="p-3">{r.indexable ? <span className="text-neon-lime font-semibold">Yes</span> : <span className="text-muted-foreground">No (noindex)</span>}</td>
                <td className="p-3"><Button size="sm" variant="outline" onClick={() => setEdit(r)} data-testid={`seo-page-edit-${r.path}`} className="rounded-full h-8">Edit</Button></td>
              </tr>
            ))}
          </tbody>
        </table>
        {rows && !rows.length && <p className="p-6 text-sm text-muted-foreground">No candidate pages. Pages appear automatically once public creator profiles match.</p>}
      </div>
      <PageEditor page={edit} onClose={() => setEdit(null)} onSaved={load} />
    </div>
  );
}
